/**
 * The hero: levels, passives, and the body that carries them.
 *
 * Three rules shape this file.
 *
 * **Experience comes from what the simulation already counts.** No new event
 * stream, no bespoke bookkeeping: kills, waves held and buildings finished are
 * already resolved in `combat.ts`, `director.ts` and `settlement.ts`, and each
 * of those calls `awardHeroXp` on its way past. If it is not already an event,
 * it is not worth experience.
 *
 * **Passives are not special cases.** Every passive in `data/heroes.ts` is a bag
 * of `Mods` keys, and `applyHeroMods` folds it into `world.mods` exactly the way
 * a relic or a tech node does. Nothing in the combat, tower or settlement
 * systems knows a hero exists — they read the same modifier bag they always did.
 * The single exception is the hero's *own* damage, which is a personal stat
 * rather than an army-wide modifier and so cannot live in a global bag.
 *
 * **Everything here is state.** Level, experience, bought skills and cooldowns
 * are hashed in `hash.ts` and saved in `save.ts`. There is no derived hero
 * cache, because a cache whose contents change what happens is not a cache.
 */

import { Team } from '../types.ts';
import { EV, LogTone, pushEvent, pushLog } from '../events.ts';
import { defaultMods, spawnUnit, type HeroState, type World } from '../world.ts';
import { createCompany } from './companies.ts';
import { heroGearDamageMul } from './items.ts';
import {
  HERO_CLASSES,
  HERO_EPITHETS,
  HERO_FIRST_NAMES,
  HERO_MAX_LEVEL,
  levelForXp,
  skillById,
  skillPointsAt,
  xpForNextLevel,
  type HeroClass,
} from '../../data/heroes.ts';
import { unitTypeById, unitTypeIndex } from '../../data/units.ts';

// ── Tuning ─────────────────────────────────────────────────────────────────

/** Flat experience for putting anything down, plus a slice of what it was worth. */
export const HERO_XP_PER_KILL = 2;
export const HERO_XP_PER_THREAT = 0.25;
/** Holding a wave is the big one, and it grows with the wave. */
export const HERO_XP_PER_WAVE = 60;
export const HERO_XP_PER_WAVE_STEP = 12;
/** A finished building. Small, but a builder's run should still level. */
export const HERO_XP_PER_BUILDING = 8;

/** Health and damage the hero gains per level over the first. */
export const HERO_HP_PER_LEVEL = 0.1;
export const HERO_DAMAGE_PER_LEVEL = 0.08;

/**
 * How far from the hero a battle skill may be placed.
 *
 * This is the whole difference between a Sovereign Power and a hero's skill: a
 * Power is cast by someone watching from above, a skill is cast by someone who
 * is standing there. Where the hero *is* becomes a decision.
 */
export const HERO_ABILITY_RANGE = 14;

/**
 * Battle skills run hotter than the Power they borrow — they cost no Will, so
 * the cooldown is the only thing holding them back, and it holds harder.
 */
export const HERO_ABILITY_COOLDOWN_MUL = 1.6;

/** Seconds before the fallen can be called back, and what calling costs. */
export const HERO_REVIVE_SECONDS = 120;
export const HERO_REVIVE_SOULS = 60;

// ── Queries ────────────────────────────────────────────────────────────────

export function heroClassOrNull(w: World): HeroClass | null {
  if (w.hero.classId === '') return null;
  return HERO_CLASSES.find((c) => c.id === w.hero.classId) ?? null;
}

export function hasHero(w: World): boolean {
  return w.hero.classId !== '';
}

/** Store index of the hero's model, or -1 if he is not on the field. */
export function heroUnit(w: World): number {
  if (w.hero.unitH < 0) return -1;
  return w.units.resolve(w.hero.unitH);
}

/** Total points earned minus points spent. */
export function heroPointsLeft(w: World): number {
  const cls = heroClassOrNull(w);
  if (!cls) return 0;
  let spent = 0;
  for (const id of w.hero.skills) spent += skillById(cls, id)?.cost ?? 0;
  return skillPointsAt(w.hero.level) - spent;
}

/** Experience into the current level, and how much the next one wants. */
export function heroXpProgress(w: World): { into: number; need: number } {
  const h = w.hero;
  if (h.level >= HERO_MAX_LEVEL) return { into: 1, need: 1 };
  const floor = xpForNextLevel(h.level - 1);
  const ceil = xpForNextLevel(h.level);
  return { into: Math.max(0, h.xp - floor), need: Math.max(1, ceil - floor) };
}

/**
 * The hero's battle skills, as fixed slots on keys 1–5.
 *
 * Ordered by the level that unlocks them, *not* by the order they were bought,
 * and the list contains every ability the class can ever have rather than only
 * the ones already learned. Both of those matter for the same reason: a hotkey
 * has to mean the same thing every time you press it. Returning only what was
 * bought, in purchase order, meant slot 2 was Rally the Banner in one run and
 * Tremor in the next — and shifted under the player's fingers mid-run the
 * moment they spent a point.
 *
 * A locked slot is still a slot. The player can see what is coming and at what
 * level, which is most of what a skill bar is for.
 */
export const HERO_ABILITY_SLOTS = 5;

export interface HeroAbilitySlot {
  /** The Sovereign Power this slot fires. */
  powerId: string;
  /** The skill that grants it. */
  skillId: string;
  name: string;
  /** Character level at which the skill may first be bought. */
  minLevel: number;
  /** Bought, and therefore usable. */
  learned: boolean;
  /** The level has been reached, so it is buyable now. */
  reached: boolean;
}

export function heroAbilitySlots(w: World): HeroAbilitySlot[] {
  const cls = heroClassOrNull(w);
  if (!cls) return [];
  const granting = cls.skills.filter((s) => s.grantsPower);
  // Level first, then the tree's own layout, then id — a total order, so the
  // slots are identical for the same class in every run.
  granting.sort((a, b) => a.minLevel - b.minLevel || a.row - b.row || a.col - b.col || (a.id < b.id ? -1 : 1));
  return granting.slice(0, HERO_ABILITY_SLOTS).map((s) => ({
    powerId: s.grantsPower as string,
    skillId: s.id,
    name: s.name,
    minLevel: s.minLevel,
    learned: w.hero.skills.includes(s.id),
    reached: w.hero.level >= s.minLevel,
  }));
}

/** The battle skills the hero has actually bought, as power ids, in slot order. */
export function heroAbilities(w: World): string[] {
  return heroAbilitySlots(w)
    .filter((s) => s.learned)
    .map((s) => s.powerId);
}

/** '' when the ability can be fired right now, otherwise the reason it cannot. */
export function heroAbilityError(w: World, id: string): string {
  if (!heroAbilities(w).includes(id)) return 'Not yours.';
  if ((w.hero.cooldowns[id] ?? 0) > 0) return 'Still cooling.';
  if (heroUnit(w) < 0) return 'He is not on the field.';
  return '';
}

/** '' when the skill can be bought, otherwise why not. */
export function heroSkillError(w: World, id: string): string {
  const cls = heroClassOrNull(w);
  if (!cls) return 'No hero.';
  /**
   * Not on a battlefield.
   *
   * A field battle is a *copy* of the character — see `carryCharacterInto` —
   * and only his experience and his cart are carried home again. A skill bought
   * mid-battle would either be lost when the field is left, or have to be
   * re-folded into a base bag that already contains it. Refusing here is the
   * version with no wrong answer, and the skill tree is a thing you sit down
   * with between fights anyway.
   */
  if (w.battle) return 'Not here. Not now.';
  const s = skillById(cls, id);
  if (!s) return 'No such skill.';
  if (w.hero.skills.includes(id)) return 'Already learned.';
  if (w.hero.level < s.minLevel) return `Needs level ${s.minLevel}.`;
  for (const r of s.requires) {
    if (!w.hero.skills.includes(r)) return `Needs ${skillById(cls, r)?.name ?? r}.`;
  }
  if (heroPointsLeft(w) < s.cost) return 'No skill points left.';
  return '';
}

/**
 * The hero's personal damage multiplier.
 *
 * Read once, from `combat.ts`, off the `heroId` already carried on the unit
 * store. Class, level and what he is *holding* are the hero's own stats — an
 * army-wide modifier bag is the wrong place for "this one man swings harder
 * than the catalogue says".
 *
 * The weapon's share is computed live from the equipped set rather than folded
 * into anything: taking the sword off makes the number 1 again by arithmetic,
 * with nothing to unwind. Its *army-wide* half goes through `world.mods` like
 * everything else — see `src/sim/systems/items.ts`.
 */
export function heroDamageMul(w: World, i: number): number {
  const id = w.units.heroId[i];
  if (id < 0) return 1;
  const cls = HERO_CLASSES[id];
  if (!cls) return 1;
  return cls.damage * (1 + HERO_DAMAGE_PER_LEVEL * (w.hero.level - 1)) * heroGearDamageMul(w);
}

// ── Modifiers ──────────────────────────────────────────────────────────────

/**
 * Keys the bag combines by taking the better of the two rather than stacking.
 * `moraleFloor` and `corpseRise` are already written this way by relics and
 * tech; heroes must not be the one source that stacks them.
 */
const MOD_BEST_OF = new Set(['moraleFloor', 'corpseRise', 'secondWind']);

/**
 * Fold a passive's modifier bag into `world.mods`.
 *
 * The combining rule is read off `defaultMods()` rather than authored twice:
 * a key that starts at 1 is a multiplier, a key that starts at 0 is additive.
 * `willMax` is the one exception and it is the tech tree's exception too — it
 * defaults to 1 but is spent as a flat bonus, and the world's own ceiling has
 * to move with it or nothing changes.
 */
function applyHeroMods(w: World, mods: Partial<Record<string, number>>): void {
  const base = defaultMods() as unknown as Record<string, number>;
  const bag = w.mods as unknown as Record<string, number>;
  for (const k of Object.keys(mods)) {
    const v = mods[k];
    if (v === undefined || !(k in bag)) continue;
    if (k === 'willMax') {
      bag.willMax += v;
      w.willMax += v;
    } else if (MOD_BEST_OF.has(k)) {
      bag[k] = Math.max(bag[k], v);
    } else if (base[k] === 0) {
      bag[k] += v;
    } else {
      bag[k] *= v;
    }
  }
}

// ── Choosing, and taking the field ─────────────────────────────────────────

/**
 * A name that is a pure function of the run's seed.
 *
 * Deliberately not drawn from `w.rng`: naming the hero must not shift the
 * stream every later decision reads from, or picking a hero would change the
 * weather.
 */
function heroName(seed: number): string {
  const s = seed >>> 0;
  const first = HERO_FIRST_NAMES[s % HERO_FIRST_NAMES.length];
  const epithet = HERO_EPITHETS[(s >>> 7) % HERO_EPITHETS.length];
  return `${first} ${epithet}`;
}

function heroHpScale(level: number): number {
  return 1 + HERO_HP_PER_LEVEL * (level - 1);
}

/**
 * Put the hero on the field as a company of one, so every order the player
 * already knows — right-click to move, right-drag to face, stances, hotkey
 * groups, the roster panel — works on him without a single new verb.
 */
function fieldHero(w: World, x: number, y: number): boolean {
  const cls = heroClassOrNull(w);
  if (!cls) return false;
  const typeIdx = unitTypeIndex(cls.unitId);
  const i = spawnUnit(w, typeIdx, Team.Player, x, y);
  if (i < 0) return false;

  const u = w.units;
  u.heroId[i] = HERO_CLASSES.indexOf(cls);
  u.maxHp[i] *= cls.hp * heroHpScale(w.hero.level);
  u.hp[i] = u.maxHp[i];
  u.speed[i] *= cls.speed;
  u.morale[i] = 1;

  const c = createCompany(w, typeIdx, Team.Player, x, y);
  c.name = w.hero.name;
  c.maxSize = 1;
  u.companyId[i] = c.id;
  u.slotIdx[i] = 0;
  c.members.push(u.handle(i));

  w.hero.unitH = u.handle(i);
  w.hero.companyId = c.id;
  return true;
}

/**
 * Put the character on the field at a chosen point.
 *
 * The public seam over `fieldHero`, for battle mode: a field battle builds a
 * fresh world and musters an army into it, and the man the campaign is about
 * has to be standing in that line or the owner's whole premise — "a character
 * on the battlefield when fighting" — is only half true. See
 * `carryCharacterInto` in `battle.ts`.
 */
export function fieldHeroAt(w: World, x: number, y: number): boolean {
  return fieldHero(w, x, y);
}

/**
 * Fold the character's *own* passives — class and bought skills — into the bag.
 *
 * Re-derivable from `data/heroes.ts` and `hero.skills` alone, which is what
 * makes it safe to call on a freshly built world: a battle world starts from
 * `defaultMods()` and gets the man himself, and nothing else. The run's relics
 * and tech deliberately stay with the settlement that bought them — those are
 * the realm's advantages, not his.
 *
 * Must be called inside `foldPermanent`, like every other permanent write.
 */
export function applyHeroPassives(w: World): void {
  const cls = heroClassOrNull(w);
  if (!cls) return;
  applyHeroMods(w, cls.startingPassive.mods);
  for (const id of w.hero.skills) {
    const s = skillById(cls, id);
    if (s?.mods) applyHeroMods(w, s.mods);
  }
}

/**
 * Choose the character this run is about. Once only, and only before the first
 * horn — a run's hero is a premise, not a mid-game purchase.
 */
export function pickHero(w: World, classId: string): boolean {
  if (w.hero.classId !== '') return false;
  if (w.wave > 0) return false;
  const cls = HERO_CLASSES.find((c) => c.id === classId);
  if (!cls) return false;
  if (cls.factions.length > 0 && !cls.factions.includes(w.faction.id)) return false;

  w.hero.classId = cls.id;
  w.hero.level = 1;
  w.hero.xp = 0;
  w.hero.name = heroName(w.config.seed);
  applyHeroMods(w, cls.startingPassive.mods);

  fieldHero(w, w.map.hearthX + 0.5, w.map.hearthY + 2.5);
  pushLog(
    w.events,
    `${w.hero.name}, ${cls.name}. ${cls.startingPassive.name}: ${cls.startingPassive.rules}`,
    LogTone.Lore,
  );
  return true;
}

/** Call the fallen back, for a price and only once the ground has cooled. */
export function reviveHero(w: World): boolean {
  const h = w.hero;
  if (h.classId === '' || !h.fallen) return false;
  if (h.reviveT > 0) return false;
  if (w.res.souls < HERO_REVIVE_SOULS) return false;
  if (!fieldHero(w, w.map.hearthX + 0.5, w.map.hearthY + 2.5)) return false;
  w.res.souls -= HERO_REVIVE_SOULS;
  h.fallen = false;
  h.reviveT = 0;
  pushLog(w.events, `${h.name} comes back. Nobody asks from where.`, LogTone.Lore);
  return true;
}

// ── Levels ─────────────────────────────────────────────────────────────────

/**
 * Experience, from the events the simulation already emits. Callers pass what
 * the thing was worth; the level table in `data/heroes.ts` decides the rest.
 */
export function awardHeroXp(w: World, amount: number): void {
  const h = w.hero;
  if (h.classId === '' || amount <= 0) return;
  h.xp += amount;

  const next = levelForXp(h.xp);
  if (next <= h.level) return;
  h.level = next;

  // The body grows with the sheet. Rebuilt from the catalogue rather than
  // multiplied onto whatever the last level left behind, so ten level-ups and
  // one level-up-to-ten produce the same hero.
  const i = heroUnit(w);
  const cls = heroClassOrNull(w);
  if (i >= 0 && cls) {
    const u = w.units;
    const frac = u.maxHp[i] > 0 ? u.hp[i] / u.maxHp[i] : 1;
    u.maxHp[i] = unitTypeById(cls.unitId).hp * cls.hp * heroHpScale(h.level) * w.mods.unitHp;
    u.hp[i] = u.maxHp[i] * frac;
    pushEvent(w.events, EV.Levelup, u.x[i], u.y[i], h.level, 0, Team.Player, 0, 0, h.classId);
  }

  pushLog(
    w.events,
    `${h.name} reaches level ${h.level}. ${heroPointsLeft(w)} skill points unspent.`,
    LogTone.Good,
  );
}

// ── Skills ─────────────────────────────────────────────────────────────────

/** Spend a skill point. Passives land in `mods`; actives become battle skills. */
export function buyHeroSkill(w: World, id: string): boolean {
  if (heroSkillError(w, id) !== '') return false;
  const cls = heroClassOrNull(w);
  if (!cls) return false;
  const s = skillById(cls, id);
  if (!s) return false;

  w.hero.skills.push(id);
  if (s.mods) applyHeroMods(w, s.mods);
  if (s.grantsPower) w.hero.cooldowns[s.grantsPower] = 0;
  pushLog(w.events, `${w.hero.name} learns ${s.name}. ${s.rules}`, LogTone.Good);
  return true;
}

// ── The tick ───────────────────────────────────────────────────────────────

/**
 * Cooldowns run down, and a hero who is no longer on the field is noticed.
 *
 * Detecting death here rather than in `killUnit` keeps `combat.ts` free of any
 * knowledge of the hero system: the handle simply stops resolving, and that is
 * the whole signal.
 */
export function stepHero(w: World, dt: number): void {
  const h: HeroState = w.hero;
  if (h.classId === '') return;

  for (const id of Object.keys(h.cooldowns)) {
    if (h.cooldowns[id] > 0) h.cooldowns[id] = Math.max(0, h.cooldowns[id] - dt);
  }

  if (h.unitH >= 0 && w.units.resolve(h.unitH) < 0) {
    h.unitH = -1;
    h.companyId = -1;
    h.fallen = true;
    h.reviveT = HERO_REVIVE_SECONDS;
    pushLog(w.events, `${h.name} goes down. The line hears it before it sees it.`, LogTone.Dire);
    w.morale -= 0.05;
  }

  if (h.fallen && h.reviveT > 0) h.reviveT = Math.max(0, h.reviveT - dt);
}
